import { renderToStaticMarkup } from 'react-dom/server'
import { cn } from '@/lib/utils'
import type { IrrigationMarkerTone } from '@/irrigation-survey/IrrigationMarker'

/*
 * Pins close enough to cover each other, drawn as one: a count, ringed in
 * the colors of what is in it (docs/irrigation.md, "Crowded pins"). Pins are
 * a fixed size on screen, so zoomed out a valve a few meters from its timer
 * sits under the timer's pin, and the map looked as if the valve was not
 * there at all.
 */

/** One pin a cluster stands for, as far as the cluster shows it. */
export interface ClusterMember {
  tone: IrrigationMarkerTone
  /** What it is, in a word or two: "valve box", "timer". */
  noun: string
  /** It has an open problem recorded on it as a whole. */
  hasOpenProblem: boolean
  /** A change to it is still waiting to reach the server. */
  pending: boolean
  /** Last visit's, shown for reference. */
  faint: boolean
}

/** Cluster diameter in CSS pixels: a little larger than a pin, so it reads as several. */
export const CLUSTER_SIZE_PX = 34

/**
 * The ring's parts, in the order they run clockwise from the top: the order
 * the water runs, then what is wrong. A valve box is equipment's color, as
 * its pin is.
 */
type Segment = 'waterSource' | 'timer' | 'equipment' | 'problem' | 'lastVisit'

const SEGMENT_ORDER: readonly Segment[] = [
  'waterSource',
  'timer',
  'equipment',
  'problem',
  'lastVisit',
]

/* Written out for the reason the pin's are: Tailwind only sees whole class names. */
const SEGMENT_CLASS: Record<Segment, string> = {
  waterSource: 'stroke-map-blue',
  timer: 'stroke-map-slate',
  equipment: 'stroke-map-teal',
  problem: 'stroke-map-crimson',
  lastVisit: 'stroke-map-crimson opacity-50',
}

/** The ring's circumference in its own units, so a share is its length. */
const RING_LENGTH = 100
const RING_RADIUS = RING_LENGTH / (2 * Math.PI)
/** The white between two parts of the ring, so neighbors stay apart. */
const RING_GAP = 2.5
/** The shortest a part is drawn, so one problem among forty still shows. */
const RING_MIN_PART = 1.5

function segmentOf(member: ClusterMember): Segment {
  if (member.faint) return 'lastVisit'
  if (member.tone === 'valveBox') return 'equipment'
  return member.tone
}

/** How many of each part of the ring, in ring order, leaving out those with none. */
export function clusterSegments(
  members: readonly ClusterMember[],
): { segment: Segment; count: number }[] {
  return SEGMENT_ORDER.map((segment) => ({
    segment,
    count: members.filter((member) => segmentOf(member) === segment).length,
  })).filter((part) => part.count > 0)
}

function plural(count: number, noun: string): string {
  if (count === 1) return `1 ${noun}`
  return `${String(count)} ${noun}${noun.endsWith('x') ? 'es' : 's'}`
}

/**
 * What a cluster holds, in words: "2 valve boxes · 1 timer · 1 problem". In
 * ring order, then most first, so it reads the way the ring is drawn.
 */
export function clusterSummary(members: readonly ClusterMember[]): string {
  const counts = new Map<
    string,
    { noun: string; rank: number; count: number }
  >()
  for (const member of members) {
    const found = counts.get(member.noun)
    if (found === undefined) {
      counts.set(member.noun, {
        noun: member.noun,
        rank: SEGMENT_ORDER.indexOf(segmentOf(member)),
        count: 1,
      })
    } else {
      found.count += 1
    }
  }
  return [...counts.values()]
    .sort(
      (a, b) =>
        a.rank - b.rank || b.count - a.count || a.noun.localeCompare(b.noun),
    )
    .map((entry) => plural(entry.count, entry.noun))
    .join(' · ')
}

/**
 * Each part of the ring as a dash along one circle of `RING_LENGTH`: where it
 * starts and how long it is drawn, its share less the gap after it.
 */
export function ringParts(
  members: readonly ClusterMember[],
): { segment: Segment; start: number; length: number }[] {
  const parts = clusterSegments(members)
  const gap = parts.length > 1 ? RING_GAP : 0
  return parts.map(({ segment, count }, index) => {
    const before = parts
      .slice(0, index)
      .reduce((sum, part) => sum + part.count, 0)
    const share = (count / members.length) * RING_LENGTH
    return {
      segment,
      start: (before / members.length) * RING_LENGTH,
      length: Math.max(share - gap, RING_MIN_PART),
    }
  })
}

/** The ring, starting at the top once the SVG is turned a quarter back. */
function RingParts({ members }: { members: readonly ClusterMember[] }) {
  return (
    <>
      {ringParts(members).map(({ segment, start, length }) => (
        <circle
          key={segment}
          className={SEGMENT_CLASS[segment]}
          cx="18"
          cy="18"
          r={RING_RADIUS}
          fill="none"
          strokeWidth="4"
          strokeDasharray={`${String(length)} ${String(RING_LENGTH - length)}`}
          strokeDashoffset={String(-start)}
        />
      ))}
    </>
  )
}

export interface IrrigationClusterProps {
  members: readonly ClusterMember[]
}

/**
 * Several pins drawn as one: how many, on a white disc with the pins' halo,
 * ringed in their colors. The pins' other two signals carry over: a red dot
 * when something in it has an open problem, and a dashed halo when a change
 * to something in it has not reached the server yet. Last visit's alone are
 * drawn faint, as their pins are.
 */
export function IrrigationCluster({ members }: IrrigationClusterProps) {
  const current = members.filter((member) => !member.faint)
  const hasOpenProblem = current.some(
    (member) => member.hasOpenProblem || member.tone === 'problem',
  )
  const pending = members.some((member) => member.pending)
  const summary = clusterSummary(members)
  return (
    <div
      className={cn('relative', current.length === 0 ? 'opacity-50' : null)}
      style={{ width: CLUSTER_SIZE_PX, height: CLUSTER_SIZE_PX }}
      title={summary}
      role="img"
      aria-label={summary}
    >
      <div
        className={cn(
          'relative h-full w-full rounded-full border-2 border-map-halo bg-map-marker-surface shadow-sm',
          pending ? 'border-dashed' : null,
        )}
      >
        <svg
          className="absolute inset-0 h-full w-full -rotate-90"
          viewBox="0 0 36 36"
          aria-hidden="true"
        >
          <RingParts members={members} />
        </svg>
        <span
          className={cn(
            'absolute inset-0 flex items-center justify-center font-semibold leading-none text-map-ink',
            members.length >= 100 ? 'text-[10px]' : 'text-[12px]',
          )}
        >
          {members.length}
        </span>
      </div>
      {hasOpenProblem ? (
        <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full border border-map-halo bg-map-crimson" />
      ) : null}
    </div>
  )
}

export function irrigationClusterHtml(props: IrrigationClusterProps): string {
  return renderToStaticMarkup(<IrrigationCluster {...props} />)
}
